import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory, useLocation} from "react-router-dom";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import {CLEARING_SYSTEM_CREATE} from "routes/Routes";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {
    getClearingSystemIdWithPrefix,
    generateDefaultWireTemplateName,
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    USABA_CLEARING_SYSTEM_CODE,
    USABA_CLEARING_SYSTEM_ID_PREFIX,
    WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";


const creditorAgentFields = [
    [
        {label: 'Agent BIC', name: 'creditorAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'creditorAgentClearingSystemCode', md: 2, type: 'select', options: [{label: 'USABA', value: 'USABA'}], conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'creditorAgentClearingSystemID', md: 4, type: 'select', dynamicOptions: true, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'creditorAgentAccountIBAN', md: 3, maxLength: 50},
    ],
    [
        {label: 'Creditor Agent Name', name: 'creditorAgentName', md: 4, maxLength: 255, required: true},
        {label: 'Agent Department', name: 'creditorAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'creditorAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'creditorAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'creditorAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'creditorAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'creditorAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'creditorAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'creditorAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'creditorAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'creditorAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'creditorAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'creditorAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'creditorAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'creditorAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

const CREDITOR_AGENT_IDENTIFIER_ERROR = 'ERROR. Please check the Note above to fill in the required fields.';
const CREDITOR_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR = `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;
// Sentinel value used as a special option inside the Clearing System ID dropdown.
// Selecting it navigates the user to the Create Clearing System screen rather than
// setting a real clearing system id value.
export const ADD_NEW_CLEARING_SYSTEM_CODE_OPTION = '__ADD_NEW_CLEARING_SYSTEM_CODE__';

const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';
const isValidUSABAClearingSystemID = (clearingSystemID) => new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test(clearingSystemID.toString().trim());

export const getCreditorAgentClearingSystemIDWithPrefix = (clearingSystemID) => {
    if (!clearingSystemID) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    const clearingSystemIDValue = clearingSystemID.toString();
    const clearingSystemIDValueUpperCase = clearingSystemIDValue.toUpperCase();

    if (USABA_CLEARING_SYSTEM_ID_PREFIX.startsWith(clearingSystemIDValueUpperCase) && clearingSystemIDValueUpperCase.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    return getClearingSystemIdWithPrefix(clearingSystemIDValue);
}

export const validateStep2 = (addWireTemplateState = {}) => {
    let errors = {}
    const {
        creditorAgentBIC,
        creditorAgentClearingSystemCode,
        creditorAgentClearingSystemID,
        creditorAgentName,
        creditorAgentTownName,
        creditorAgentCountry,
    } = addWireTemplateState;

    if (!hasValue(creditorAgentName)) {
        errors['creditorAgentName'] = 'Please enter creditor agent name'
    }

    const hasBIC = hasValue(creditorAgentBIC);
    const hasClearingSystemCode = hasValue(creditorAgentClearingSystemCode);
    const hasClearingSystemID = hasValue(creditorAgentClearingSystemID);
    const hasTown = hasValue(creditorAgentTownName);
    const hasCountry = hasValue(creditorAgentCountry);

    if (!hasBIC && (!hasClearingSystemCode || !hasClearingSystemID || !hasTown || !hasCountry)) {
        errors['creditorAgentBIC'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        if (!hasClearingSystemCode) {
            errors['creditorAgentClearingSystemCode'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasClearingSystemID) {
            errors['creditorAgentClearingSystemID'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasTown) {
            errors['creditorAgentTownName'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
        if (!hasCountry) {
            errors['creditorAgentCountry'] = CREDITOR_AGENT_IDENTIFIER_ERROR;
        }
    }

    if (creditorAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && hasClearingSystemID && !isValidUSABAClearingSystemID(creditorAgentClearingSystemID)) {
        errors['creditorAgentClearingSystemID'] = CREDITOR_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR
    }

    return errors
}

export default function AddNewWireTemplateStep2() {

    const history = useHistory();
    const location = useLocation();
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequest, makeGetRequestNoParams} = useContext(SecurityContext);
    const [countries, setCountries] = useState([]);
    const [clearingSystemList, setClearingSystemList] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    // Cache of clearing system id lists keyed by clearing system code so the API
    // is only called once per code (avoids re-fetching on every code change).
    const clearingSystemCacheRef = useRef({});

    const currentStepPage = '2'
    const backStepPage = '1'
    const nextStepPage = '3'

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = () => {}

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    const getClearingSystemList = useCallback((clearingSystemCode) => {
        if (!clearingSystemCode) {
            setClearingSystemList([]);
            return;
        }

        // Serve from cache if this clearing system code was already fetched.
        if (clearingSystemCacheRef.current[clearingSystemCode]) {
            setClearingSystemList(clearingSystemCacheRef.current[clearingSystemCode]);
            return;
        }

        const onSuccessResponse = (res) => {
            const data = res.data || [];
            clearingSystemCacheRef.current[clearingSystemCode] = data;
            setClearingSystemList(data);
        }

        const onErrorResponse = (err) => {
            console.log("Error on get clearing system ids by system code: ", err);
        }

        makeGetRequest(URL.CLEARING_SYSTEM_FIND_SYSTEM_IDS_BY_SYSTEM_CODE_URL, {clearingSystemCode}, onSuccessResponse, onErrorResponse)
    }, [makeGetRequest])

    // Fetch the clearing system details for the selected Clearing System Code and ID
    // and populate the creditor agent name, town, province and country fields.
    const getClearingSystemDetails = useCallback((clearingSystemCode, clearingSystemId) => {
        if (!clearingSystemCode || !clearingSystemId) {
            return;
        }

        const onSuccessResponse = (res) => {
            const data = res.data;
            if (data) {
                setAddWireTemplateState(previousState => ({
                    ...previousState,
                    creditorAgentName: data.agentName || '',
                    creditorAgentTownName: data.agentCity || '',
                    creditorAgentProvince: data.agentProvince || '',
                    creditorAgentCountry: data.agentCountry || '',
                }));
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get clearing system by system code and system id: ", err);
        }

        makeGetRequest(URL.CLEARING_SYSTEM_FIND_BY_SYSTEM_CODE_AND_SYSTEM_ID_URL, {clearingSystemCode, clearingSystemId}, onSuccessResponse, onErrorResponse)
    }, [makeGetRequest, setAddWireTemplateState])

    // Build the Clearing System ID dropdown options from the list returned by the API
    // (already filtered by the selected Clearing System Code). For USABA, keep the FW prefix.
    const clearingSystemIdOptions = useMemo(() => {
        const selectedCode = addWireTemplateState.creditorAgentClearingSystemCode;
        if (!selectedCode) {
            return [];
        }

        return clearingSystemList
            .map(item => {
                const clearingSystemId = selectedCode === USABA_CLEARING_SYSTEM_CODE
                    ? getCreditorAgentClearingSystemIDWithPrefix(item.clearingSystemId)
                    : item.clearingSystemId;
                return {label: clearingSystemId, value: clearingSystemId};
            });
    }, [clearingSystemList, addWireTemplateState.creditorAgentClearingSystemCode])

    useEffect(() => {
        getCountries()
    }, [getCountries])
    useEffect(() => {
        getClearingSystemList(addWireTemplateState.creditorAgentClearingSystemCode)
    }, [getClearingSystemList, addWireTemplateState.creditorAgentClearingSystemCode])
    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])

    // When returning from the Create Clearing System screen, pre-populate the
    // Creditor Agent Clearing System Code and ID with the values that were just
    // created, along with the Agent Name/City/Country entered on the create
    // screen so the user doesn't have to re-enter them here. The component
    // remounts on return, so the ID dropdown refetches from the API (now
    // including the new record) and can display the selected id.
    useEffect(() => {
        const createdClearingSystem = location.state && location.state.createdClearingSystem;
        if (createdClearingSystem && createdClearingSystem.clearingSystemCode) {
            setAddWireTemplateState(previousState => ({
                ...previousState,
                creditorAgentClearingSystemCode: createdClearingSystem.clearingSystemCode,
                creditorAgentClearingSystemID: createdClearingSystem.clearingSystemId,
                creditorAgentName: createdClearingSystem.agentName || previousState.creditorAgentName,
                creditorAgentTownName: createdClearingSystem.agentCity || previousState.creditorAgentTownName,
                creditorAgentProvince: createdClearingSystem.agentProvince || previousState.creditorAgentProvince,
                creditorAgentCountry: createdClearingSystem.agentCountry || previousState.creditorAgentCountry,
            }));
            // Clear the navigation state so the values are not re-applied on re-render.
            history.replace(location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;

        // Selecting the special "Add new clearing system code" option in the
        // Clearing System ID dropdown navigates to the Create Clearing System screen
        // instead of updating the field value.
        if (name === 'creditorAgentClearingSystemID' && value === ADD_NEW_CLEARING_SYSTEM_CODE_OPTION) {
            goToAddNewClearingSystemCode();
            return;
        }

        const newAddWireTemplateState = {...addWireTemplateState, [name]: value};

        // Clearing System ID options depend on the selected Clearing System Code,
        // so reset the previously selected ID whenever the code changes.
        if (name === 'creditorAgentClearingSystemCode') {
            newAddWireTemplateState.creditorAgentClearingSystemID = '';
        }

        if (name === 'creditorAgentName') {
            newAddWireTemplateState.templateName = generateDefaultWireTemplateName({
                accountDisplayName: addWireTemplateState.accountDisplayName,
                settlementCurrency: addWireTemplateState.settlementCurrency,
                creditorAgentName: value
            });
        }

        setAddWireTemplateState(newAddWireTemplateState);
        if (formErrors[name]) {
            setFormErrors(previousErrors => ({...previousErrors, [name]: ''}))
        }

        // When a Clearing System ID is selected with a non-empty value, fetch the
        // clearing system details and populate the creditor agent name, town,
        // province and country fields. When the value is cleared, reset those fields.
        if (name === 'creditorAgentClearingSystemID') {
            if (hasValue(value)) {
                getClearingSystemDetails(newAddWireTemplateState.creditorAgentClearingSystemCode, value);
            } else {
                setAddWireTemplateState(previousState => ({
                    ...previousState,
                    creditorAgentName: '',
                    creditorAgentTownName: '',
                    creditorAgentProvince: '',
                    creditorAgentCountry: '',
                }));
            }
        }
    }


    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        setFormErrors({});
    }

    const handleBack = (e) => {
        e.preventDefault();
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=backStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
    }

    // Navigate to the Create Clearing System screen, remembering this step so the
    // user is returned here after a successful create. The wizard field values live
    // in WireTemplatesContext (and session storage), so they survive the round trip.
    const goToAddNewClearingSystemCode = () => {
        history.push(CLEARING_SYSTEM_CREATE, {returnTo: location.pathname});
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep2(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }

        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']= nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    const renderRequiredMarker = (field) => {
        if (field.required) {
            return <span className='red-cell'>*</span>;
        }

        return field.conditionallyRequired ? <span className='grey-cell'>*</span> : null;
    };

    const renderField = (field) => (
        <Col md={field.md} key={field.name}>
            <Form.Group controlId={`formWireTemplateStep2${field.name}`} className={field.type === 'country' || field.type === 'select' ? 'td-select' : ''}>
                <Form.Label column={false} className="filter-input-label">{field.label} {renderRequiredMarker(field)}</Form.Label>
                {field.type === 'country' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </Form.Control> : field.type === 'select' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        {field.name === 'creditorAgentClearingSystemID' &&
                            <option value={ADD_NEW_CLEARING_SYSTEM_CODE_OPTION} style={{color: '#008a00', fontWeight: 'bold'}}>Add new clearing system code &gt;</option>
                        }
                        <option value={''}>Select</option>
                        {(field.dynamicOptions ? clearingSystemIdOptions : field.options).map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Control> :
                    <Form.Control
                        name={field.name}
                        value={addWireTemplateState[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={handleChange}
                        isInvalid={!!formErrors[field.name]}
                    />
                }
                <Form.Control.Feedback type={'invalid'}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    )

    const hasFormErrors = Object.keys(formErrors).some(Boolean);

    return (
        <WireTemplateWizardLayout currentStep={2} stepTitle="Creditor Agent Details">
            <ShiftEnterPopupMessage />
            <Form>
                <div className={`awt-note ${hasFormErrors ? 'awt-note--error' : ''}`} role="note">
                    <b>Note:</b> Please populate CreditorAgentBIC and CreditorAgentName OR CreditorAgentClearingSystemCode, CreditorAgentClearingSystemIDs, CreditorAgentName, CreditorAgentTown and CreditorAgentCountry.
                    <p></p>
                    <b>Note:</b> Please populate Creditor Agent Street Name and Creditor Agent Province where possible.
                </div>
                {creditorAgentFields.map((fieldRow, index) => (
                    <Row key={index}>
                        {fieldRow.map(renderField)}
                    </Row>
                ))}

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}

